import React from "react";
import { Loader2, RefreshCw, Wrench, CheckCircle2, XCircle, Clock, Info, Lock, Unlock, Link2 } from "lucide-react";

function StateBadge({ ok, label }) {
  if (ok) return <span className="inline-flex items-center gap-1 text-[11px] text-emerald-400 mono"><CheckCircle2 size={11} /> {label}</span>;
  if (label === "missing" || label === "mismatch") return <span className="inline-flex items-center gap-1 text-[11px] text-red-400 mono"><XCircle size={11} /> {label}</span>;
  return <span className="inline-flex items-center gap-1 text-[11px] text-amber-400 mono"><Clock size={11} /> {label}</span>;
}

function cfLabel(row) {
  if (row.cf_ok) return "written";
  return row.cf_record_id ? "mismatch" : "missing";
}

function HttpsBadges({ row }) {
  const h = row.https || {};
  let https;
  if (row.https_ok) https = <span className="inline-flex items-center gap-1 text-[11px] text-emerald-400 mono"><Lock size={11} /> HTTPS · cert valid</span>;
  else if (h.error) https = <span className="inline-flex items-center gap-1 text-[11px] text-red-400 mono"><XCircle size={11} /> HTTPS failed</span>;
  else if (h.tls_status === "pending") https = <span className="inline-flex items-center gap-1 text-[11px] text-amber-400 mono"><Clock size={11} /> HTTPS cert pending</span>;
  else https = <span className="inline-flex items-center gap-1 text-[11px] text-amber-400 mono"><Unlock size={11} /> HTTP only</span>;
  const linked = h.associated === true;
  return (
    <div className="mt-1 flex flex-col gap-0.5">
      <span data-testid="sparkpost-tracking-https">{https}</span>
      <span data-testid="sparkpost-tracking-assoc" className={`inline-flex items-center gap-1 text-[11px] mono ${linked ? "text-emerald-400" : "text-amber-400"}`}>
        <Link2 size={11} /> {linked ? "linked to sending domain" : "not linked yet"}
      </span>
    </div>
  );
}

function MatrixRow({ row }) {
  return (
    <tr className="border-t border-zinc-800/60 hover:bg-zinc-900/40" data-testid={`sparkpost-row-${row.kind}`}>
      <td className="px-3 py-2 align-top">
        <div className="text-zinc-200 font-medium">{row.label}</div>
        {row.notes && <div className="mt-1 flex items-start gap-1 text-[10px] text-amber-400/80"><Info size={10} className="mt-[1px] shrink-0" /><span>{row.notes}</span></div>}
      </td>
      <td className="px-3 py-2 mono text-zinc-400">{row.type}</td>
      <td className="px-3 py-2 mono text-zinc-400 break-all max-w-[280px]">{row.name}</td>
      <td className="px-3 py-2">
        <StateBadge ok={row.cf_ok} label={cfLabel(row)} />
        {row.expected && (
          <details className="mt-1">
            <summary className="text-[10px] text-zinc-500 cursor-pointer hover:text-zinc-300">expected</summary>
            <div className="mono text-[10px] text-zinc-500 break-all mt-1 max-w-[240px]">{row.expected}</div>
            {row.cf_content && row.cf_content !== row.expected && <div className="mono text-[10px] text-amber-400/80 break-all mt-1 max-w-[240px]">actual: {row.cf_content}</div>}
          </details>
        )}
      </td>
      <td className="px-3 py-2">
        <StateBadge ok={row.sparkpost_verified} label={row.sparkpost_verified ? "verified" : (row.sparkpost_status || "pending")} />
        {row.kind === "tracking_cname" && <HttpsBadges row={row} />}
      </td>
    </tr>
  );
}

function MatrixActions({ busy, busyRow, fixDns, verifyAll, refreshMatrix }) {
  return (
    <div className="flex items-center gap-2">
      <button data-testid="sparkpost-fix-dns-btn" onClick={fixDns} disabled={busy} title="Write any missing records into Cloudflare" className="inline-flex items-center gap-1 border border-emerald-700 text-emerald-300 hover:border-emerald-400 hover:text-emerald-200 px-3 py-1.5 rounded-sm text-xs font-medium disabled:opacity-50">
        {busyRow === "all" ? <Loader2 size={12} className="animate-spin" /> : <Wrench size={12} />} Fix DNS
      </button>
      <button data-testid="sparkpost-verify-all-btn" onClick={verifyAll} disabled={busy} title="Trigger SparkPost-side verification" className="inline-flex items-center gap-1 border border-zinc-700 hover:border-white text-zinc-300 hover:text-white px-3 py-1.5 rounded-sm text-xs font-medium disabled:opacity-50">
        {busyRow === "verify" ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Verify all
      </button>
      <button data-testid="sparkpost-refresh-matrix-btn" onClick={refreshMatrix} disabled={busy} title="Refresh matrix" className="inline-flex items-center gap-1 text-zinc-400 hover:text-zinc-100 px-2 py-1.5 rounded-sm text-xs disabled:opacity-50"><RefreshCw size={12} /></button>
    </div>
  );
}

/** Connected state: the 4-row DNS matrix (Cloudflare + SparkPost status) with Fix / Verify / Refresh. */
export function SparkPostDnsMatrix({ matrix, busy, busyRow, fixDns, verifyAll, refreshMatrix }) {
  return (
    <div className="mt-2">
      <div className="flex items-center justify-between mb-2">
        <div className="label-tiny">DNS records ({matrix.length})</div>
        <MatrixActions busy={busy} busyRow={busyRow} fixDns={fixDns} verifyAll={verifyAll} refreshMatrix={refreshMatrix} />
      </div>
      <div className="border border-zinc-800 rounded-sm overflow-hidden" data-testid="sparkpost-dns-matrix">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-zinc-950/60 text-zinc-500 uppercase tracking-wider text-[10px]">
              <th className="text-left px-3 py-2">Record</th><th className="text-left px-3 py-2">Type</th><th className="text-left px-3 py-2">Name</th><th className="text-left px-3 py-2">Cloudflare</th><th className="text-left px-3 py-2">SparkPost</th>
            </tr>
          </thead>
          <tbody>
            {matrix.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-4 text-center text-zinc-500 mono">{busy ? "Loading matrix…" : "No DNS matrix yet — hit \"Verify all\" to fetch"}</td></tr>
            )}
            {matrix.map((row) => <MatrixRow key={row.kind} row={row} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
}
